import type { ReactNode } from "react";

import { Badge } from "@/components/ui";
import { cn } from "@/lib/utils";
import { formatMs, formatUsd } from "@/utils/format";
import type { TraceMetrics as Metrics } from "@/utils/trace/model";

/**
 * The run summary as one line of badges. Shared by every screen that shows a
 * trace — Conversations beside the message, Test Result beside the score
 * (fe/TEST_CASES_RESULT.md §4.1) — so the two read the same.
 *
 * **Two kinds of badge.** Sub-agents and citation workers appear only when
 * there were some: `0 sub-agent(s)` on a run that never delegated is noise.
 * Errors always appear, because "no errors" is the information.
 */
export function TraceMetricsRow({
  metrics,
  className,
  children,
}: {
  metrics: Metrics;
  className?: string;
  /** Badges a screen adds on the same line — Test Result's commit. */
  children?: ReactNode;
}) {
  return (
    <div className={cn("mt-2 mb-2.5 flex flex-wrap gap-2", className)}>
      <Badge>TTFT {formatMs(metrics.ttftMs)}</Badge>
      <Badge>span {formatMs(metrics.totalMs)}</Badge>
      <Badge>{metrics.turnCount} turn(s)</Badge>
      <Badge>{metrics.toolCount} tool call(s)</Badge>
      {metrics.subAgentCount > 0 && <Badge>{metrics.subAgentCount} sub-agent(s)</Badge>}
      {metrics.citationWorkerCount > 0 && (
        <Badge>{metrics.citationWorkerCount} citation worker(s)</Badge>
      )}
      <Badge
        className={cn(
          metrics.errorCount > 0 &&
            "border-destructive-line bg-destructive-surface text-destructive",
        )}
      >
        {metrics.errorCount} error(s)
      </Badge>
      {metrics.tokensIn !== null && (
        <Badge>
          tokens {metrics.tokensIn}
          {metrics.tokensOut !== null ? ` / ${metrics.tokensOut}` : ""}
        </Badge>
      )}
      {/* Reasoning tokens are billed and are often the largest line item, but
          they are counted in neither the prompt nor the completion total. */}
      {metrics.tokensThoughts !== null && metrics.tokensThoughts > 0 && (
        <Badge>thinking {metrics.tokensThoughts}</Badge>
      )}
      {/* Both are inside `tokens` above; shown because they price differently:
          a read at a tenth of the input rate, a write above it. */}
      {metrics.tokensCached !== null && metrics.tokensCached > 0 && (
        <Badge>cached {metrics.tokensCached}</Badge>
      )}
      {metrics.tokensCacheWrite !== null && metrics.tokensCacheWrite > 0 && (
        <Badge>cache write {metrics.tokensCacheWrite}</Badge>
      )}
      {/* Provider-billed, summed over the calls that report it (OpenRouter);
          Gemini calls report none, so a mixed trace undercounts. */}
      {metrics.costUsd !== null && <Badge>billed {formatUsd(metrics.costUsd)}</Badge>}
      {children}
    </div>
  );
}
